import { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { Menu, X } from 'lucide-react'
import { Button } from '@/shared/ui/button'
import { C } from '../lib/landingColors'
import { scrollTo } from '../lib/landingUtils'
import { useLandingLocale, useLandingRoute, type MarketingLocale, type Multilingual } from '../lib/marketingLocale'
import { CTA_REGISTER } from '../content/landingCopy'
import { LandingLanguageSelect } from './LandingLanguageSelect'
import { LogoMark } from './LogoMark'

/**
 * Six-way, because all four items point at landing sections and the landing is
 * six-language. On a document the route still overrides this to en/de.
 */
const NAV_LINKS: Multilingual<{ label: string; id: string }[]> = {
  en: [
    { label: 'How it works', id: 'pipeline' },
    { label: 'Features', id: 'features' },
    { label: 'Pricing', id: 'pricing' },
    { label: 'Contact', id: 'contact' },
  ],
  de: [
    { label: 'So funktioniert’s', id: 'pipeline' },
    { label: 'Funktionen', id: 'features' },
    { label: 'Preise', id: 'pricing' },
    { label: 'Kontakt', id: 'contact' },
  ],
  fr: [
    { label: 'Comment ça marche', id: 'pipeline' },
    { label: 'Fonctionnalités', id: 'features' },
    { label: 'Tarifs', id: 'pricing' },
    { label: 'Contact', id: 'contact' },
  ],
  it: [
    { label: 'Come funziona', id: 'pipeline' },
    { label: 'Funzioni', id: 'features' },
    { label: 'Prezzi', id: 'pricing' },
    { label: 'Contatti', id: 'contact' },
  ],
  es: [
    { label: 'Cómo funciona', id: 'pipeline' },
    { label: 'Funciones', id: 'features' },
    { label: 'Precios', id: 'pricing' },
    { label: 'Contacto', id: 'contact' },
  ],
  pt: [
    { label: 'Como funciona', id: 'pipeline' },
    { label: 'Recursos', id: 'features' },
    { label: 'Preços', id: 'pricing' },
    { label: 'Contato', id: 'contact' },
  ],
  hu: [
    { label: 'Hogyan működik', id: 'pipeline' },
    { label: 'Funkciók', id: 'features' },
    { label: 'Árak', id: 'pricing' },
    { label: 'Kapcsolat', id: 'contact' },
  ],
  nl: [
    { label: 'Hoe het werkt', id: 'pipeline' },
    { label: 'Functies', id: 'features' },
    { label: 'Prijzen', id: 'pricing' },
    { label: 'Contact', id: 'contact' },
  ],
  pl: [
    { label: 'Jak to działa', id: 'pipeline' },
    { label: 'Funkcje', id: 'features' },
    { label: 'Cennik', id: 'pricing' },
    { label: 'Kontakt', id: 'contact' },
  ],
  cs: [
    { label: 'Jak to funguje', id: 'pipeline' },
    { label: 'Funkce', id: 'features' },
    { label: 'Ceník', id: 'pricing' },
    { label: 'Kontakt', id: 'contact' },
  ],
}

/**
 * The hamburger's accessible name — the last English literal that was left
 * inline here, so a German (and now French, Italian, Spanish, Portuguese)
 * reader's screen reader announced "Open menu" over translated copy.
 */
const MENU_ARIA: Multilingual<{ open: string; close: string }> = {
  en: { open: 'Open menu', close: 'Close menu' },
  de: { open: 'Menü öffnen', close: 'Menü schliessen' },
  fr: { open: 'Ouvrir le menu', close: 'Fermer le menu' },
  it: { open: 'Apri il menu', close: 'Chiudi il menu' },
  es: { open: 'Abrir el menú', close: 'Cerrar el menú' },
  pt: { open: 'Abrir o menu', close: 'Fechar o menu' },
  hu: { open: 'Menü megnyitása', close: 'Menü bezárása' },
  nl: { open: 'Menu openen', close: 'Menu sluiten' },
  pl: { open: 'Otwórz menu', close: 'Zamknij menu' },
  cs: { open: 'Otevřít nabídku', close: 'Zavřít nabídku' },
}

/** Same words as the app's own `logIn` key in each locale bundle. */
const LOG_IN: Multilingual<string> = {
  en: 'Log in',
  de: 'Anmelden',
  fr: 'Se connecter',
  it: 'Accedi',
  es: 'Iniciar sesión',
  pt: 'Entrar',
  hu: 'Bejelentkezés',
  nl: 'Inloggen',
  pl: 'Zaloguj się',
  cs: 'Přihlásit se',
}

/**
 * The landing route. `/` is the same page for a logged-out visitor. On a market
 * URL (`/hu`, decision D26) the landing is that URL instead, so the logo and the
 * section links keep a Hungarian reader on the Hungarian page rather than
 * dropping them onto `/welcome` in whatever language their browser reports.
 */
const DEFAULT_LANDING_PATH = '/welcome'

/**
 * `locale` overrides the visitor's UI locale on pages whose language comes from
 * the URL (the /de marketing documents and the comparison pairs) and is
 * therefore still the two-way `MarketingLocale`. The hook is the fallback for
 * the landing page, which has no per-language URL of its own and renders in the
 * visitor's own language — one of six. A route locale is assignable into the
 * six-way one, so the same nav serves both without a branch.
 *
 * The four section items are real `<Link>`s to `/welcome#<id>`, not spans with
 * an onClick. They used to be spans calling `scrollTo(id)` directly, which
 * worked on the landing page and silently did NOTHING everywhere else: the ids
 * (`pipeline`, `features`, `pricing`, `contact`) only exist on the landing, so
 * from /founding, /press or a comparison page all four were dead. Being links
 * also makes them keyboard-focusable, middle-clickable and crawlable, none of
 * which a span is. On the landing itself the click is intercepted so the smooth
 * scroll is preserved; anywhere else the Link navigates and the landing page's
 * own hash effect does the scrolling on arrival.
 */
export function LandingNav({ locale: routeLocale }: { locale?: MarketingLocale } = {}) {
  const uiLocale = useLandingLocale()
  const { pathname } = useLocation()
  const landingPath = useLandingRoute()?.path ?? DEFAULT_LANDING_PATH
  const locale = routeLocale ?? uiLocale
  const navLinks = NAV_LINKS[locale]
  const [scrolled, setScrolled] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  const onLanding = pathname === landingPath || pathname === '/'

  /**
   * Landing page: keep the smooth in-page scroll and leave the URL alone.
   * Anywhere else: fall through, so the Link performs a real navigation to
   * /welcome#<id>.
   */
  const scrollIfOnLanding = (event: React.MouseEvent, id: string) => {
    if (!onLanding) return
    event.preventDefault()
    scrollTo(id)
  }

  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 20)
    window.addEventListener('scroll', h, { passive: true })
    return () => window.removeEventListener('scroll', h)
  }, [])

  // pt-safe clears the iOS status bar / notch in a standalone PWA (same approach as
  // AppLayout's mobile header) — without it the fixed bar sat under the status bar
  // after logout and the hamburger was pushed off-screen (round-2 item 9a).
  return (
    <nav className={`fixed top-0 inset-x-0 z-50 pt-safe transition-colors duration-300 ${scrolled ? 'sm:backdrop-blur-md shadow-lg' : ''}`}
      style={{ backgroundColor: scrolled ? C.bg : 'transparent', borderBottom: scrolled ? `1px solid ${C.border}` : 'none' }}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 flex items-center justify-between h-16">
        {/* /welcome, NOT "/": the root is ProtectedRoute → HomeRedirect, so with a
            live session in the same browser the logo dropped a marketing reader
            straight into their studio app. The marketing logo stays on marketing;
            on the landing itself it scrolls back to the top. */}
        <Link to={landingPath} className="flex items-center gap-2.5"
          onClick={e => {
            if (!onLanding) return
            e.preventDefault()
            window.scrollTo({ top: 0, behavior: 'smooth' })
          }}>
          <LogoMark className="w-7 h-7" />
          <span className="text-lg font-bold tracking-tight" style={{ color: C.text }}>
            Studio<span style={{ color: C.primary }}>Flow</span>
          </span>
        </Link>

        <div className="hidden md:flex items-center gap-8">
          {navLinks.map(s => (
            <Link key={s.id} to={`${landingPath}#${s.id}`}
              className="text-sm font-medium transition-colors"
              style={{ color: C.muted }}
              onMouseEnter={e => (e.currentTarget.style.color = C.text)}
              onMouseLeave={e => (e.currentTarget.style.color = C.muted)}
              onClick={e => scrollIfOnLanding(e, s.id)}>{s.label}</Link>
          ))}
        </div>

        <div className="hidden md:flex items-center gap-3">
          {/* Landing only: the paired documents carry the EN/DE MarketingLanguageSwitch,
              and a six-way selector there would offer languages the page doesn't exist in. */}
          {onLanding && <LandingLanguageSelect />}
          <Link to="/login"><Button variant="ghost" className="hover:bg-white/10" style={{ color: C.muted }}>{LOG_IN[locale]}</Button></Link>
          <Link to="/register">
            <Button className="text-white" style={{ background: C.primary, boxShadow: `0 6px 20px ${C.primary}40` }}>{CTA_REGISTER[locale]}</Button>
          </Link>
        </div>

        <button
          className="md:hidden"
          style={{ color: C.muted }}
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label={menuOpen ? MENU_ARIA[locale].close : MENU_ARIA[locale].open}
          aria-expanded={menuOpen}
        >
          {menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {menuOpen && (
        <div className="md:hidden px-6 pb-6 space-y-3" style={{ backgroundColor: `${C.bg}f8`, borderBottom: `1px solid ${C.border}` }}>
          {navLinks.map(s => (
            <Link key={s.id} to={`${landingPath}#${s.id}`} className="block py-2 text-sm font-medium"
              style={{ color: C.muted }}
              onClick={e => { scrollIfOnLanding(e, s.id); setMenuOpen(false) }}>{s.label}</Link>
          ))}
          {onLanding && <LandingLanguageSelect className="-ml-2" />}
          <div className="pt-2 flex flex-col gap-2">
            {/* bg-transparent overrides the outline variant's white bg-background —
                otherwise the near-white C.text label sat on a white button and read
                as invisible/wrong on the dark mobile menu (round-2 item 9d). */}
            <Link to="/login"><Button variant="outline" className="w-full bg-transparent hover:bg-white/10" style={{ borderColor: C.border, color: C.text }}>{LOG_IN[locale]}</Button></Link>
            <Link to="/register"><Button className="w-full text-white" style={{ background: C.primary }}>{CTA_REGISTER[locale]}</Button></Link>
          </div>
        </div>
      )}
    </nav>
  )
}
